import React, { useState, useEffect, useRef, useCallback } from 'react';
import { CalendarDays, Copy, Eye, FileText, FolderDown, HardDrive, Trash2, UploadCloud, UserRound, X } from 'lucide-react';
import { useLanguage } from '../../assets/js/LanguageContext';
import ActionMenu from '../common/ActionMenu';
import Loader from '../common/Loader';
import Modal from '../common/Modal';
import SectionEyebrow from '../common/SectionEyebrow';
import '../../assets/styles/DashboardList.css';
import '../../assets/styles/TemplateBuilder.css';
import '../../assets/styles/ProfileArea.css';
import '../../assets/styles/AiDocsArea.css';

const API_BASE_URL = process.env.REACT_APP_API_URL;

const ALLOWED_EXTENSIONS = ['pdf', 'docx', 'txt', 'md'];

const isAllowedFile = (name) => ALLOWED_EXTENSIONS.includes((name || '').split('.').pop().toLowerCase());

const formatBytes = (n) => {
  if (!n && n !== 0) return '—';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
};

const formatDate = (s) => {
  if (!s) return '—';
  try { return new Date(s).toLocaleString(); } catch (_) { return s; }
};

const AiDocuments = () => {
  const { t } = useLanguage();
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [contentDoc, setContentDoc] = useState(null); // documento abierto para inspección
  const [contentLoading, setContentLoading] = useState(false);
  const [docToDelete, setDocToDelete] = useState(null);
  const [selectedName, setSelectedName] = useState('');
  const [alert, setAlert] = useState(null);
  const fileInputRef = useRef(null);

  const token = localStorage.getItem('token');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${API_BASE_URL}/ai/documents`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        throw new Error(d.error || t('ai_docs_error_load'));
      }
      setDocs(await res.json());
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const rejectFile = () => {
    setAlert({ title: t('upload_format_title'), text: t('ai_docs_unsupported') });
    if (fileInputRef.current) fileInputRef.current.value = '';
    setSelectedName('');
  };

  const pickFile = (file) => {
    if (!file) return;
    if (!isAllowedFile(file.name)) {
      rejectFile();
      return;
    }
    setSelectedName(file.name);
  };

  const handleDropFile = (event) => {
    event.preventDefault();
    const file = event.dataTransfer.files && event.dataTransfer.files[0];
    if (!file) return;
    if (!isAllowedFile(file.name)) {
      rejectFile();
      return;
    }
    try {
      const transfer = new DataTransfer();
      transfer.items.add(file);
      if (fileInputRef.current) fileInputRef.current.files = transfer.files;
      setSelectedName(file.name);
    } catch (err) {
      console.error('No se pudo asignar el archivo soltado:', err);
    }
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    const file = fileInputRef.current?.files?.[0];
    if (!file) {
      setError(t('ai_docs_select_file'));
      return;
    }
    if (!isAllowedFile(file.name)) {
      rejectFile();
      return;
    }
    setUploading(true);
    setError('');
    setSuccess('');
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await fetch(`${API_BASE_URL}/ai/documents`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || t('ai_docs_error_upload'));
      setSuccess(`"${data.nombre_archivo}" ${t('ai_docs_indexed_prefix')} (${Number(data.bytes_texto || 0).toLocaleString()} ${t('ai_docs_indexed_suffix')}).`);
      if (fileInputRef.current) fileInputRef.current.value = '';
      load();
    } catch (err) {
      setError(err.message);
    } finally {
      setUploading(false);
    }
  };

  const handleImportFolder = async () => {
    setImporting(true);
    setError('');
    setSuccess('');
    try {
      const res = await fetch(`${API_BASE_URL}/ai/documents/import-folder`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || t('ai_docs_error_import'));
      const summary = `${t('ai_docs_imported')}: ${data.imported.length}. ${t('ai_docs_skipped')}: ${data.skipped.length}. ${t('ai_docs_failed')}: ${data.failed.length}.`;
      setSuccess(summary);
      load();
    } catch (err) {
      setError(err.message);
    } finally {
      setImporting(false);
    }
  };

  // Abre un modal con el texto extraído de un documento.
  // Útil para que el admin verifique qué quedó indexado tras la extracción.
  const handleViewContent = async (id, name) => {
    setContentDoc({ id_documento: id, nombre_archivo: name, contenido: null });
    setContentLoading(true);
    setError('');
    try {
      const res = await fetch(`${API_BASE_URL}/ai/documents/${id}/content`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || t('ai_docs_error_load'));
      setContentDoc(data);
    } catch (err) {
      setError(err.message);
      setContentDoc(null);
    } finally {
      setContentLoading(false);
    }
  };

  const handleCopyContent = async () => {
    if (!contentDoc?.contenido) return;
    try {
      await navigator.clipboard.writeText(contentDoc.contenido);
      setSuccess(t('ai_docs_content_copied') || 'Texto copiado al portapapeles.');
      setTimeout(() => setSuccess(''), 2200);
    } catch (_) { /* ignore */ }
  };

  const handleDelete = async (id, name) => {
    setError('');
    setSuccess('');
    try {
      const res = await fetch(`${API_BASE_URL}/ai/documents/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || t('ai_docs_error_delete'));
      setDocs((prev) => prev.filter((d) => d.id_documento !== id));
      setSuccess(`"${name}" ${t('ai_docs_deleted')}.`);
    } catch (err) {
      setError(err.message);
    }
  };

  const openDelete = (doc) => {
    setDocToDelete(doc);
  };

  const confirmDelete = () => {
    if (docToDelete) handleDelete(docToDelete.id_documento, docToDelete.nombre_archivo);
    setDocToDelete(null);
  };

  return (
    <div className="home-container tp-dash tp-docs">
      <div className="dashboard-container">
        <header className="tp-dash__head">
          <div className="tp-dash__heading">
            <SectionEyebrow>{t('adminheader_ai_docs')}</SectionEyebrow>
            <h1 className="tp-docs__title">{t('ai_docs_title')}</h1>
            <p className="tp-dash__lead">{t('ai_docs_intro')}</p>
          </div>
        </header>

        <form className="tp-form tp-docs__upload" onSubmit={handleUpload}>
          <label
            className={`tp-upload__drop ${selectedName ? 'is-filled' : ''}`.trim()}
            onDragOver={(event) => event.preventDefault()}
            onDrop={handleDropFile}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept=".pdf,.docx,.txt,.md"
              disabled={uploading}
              className="tp-sr-only"
              onChange={(e) => pickFile(e.target.files[0])}
            />
            <span className="tp-upload__icon" aria-hidden="true">
              <FileText size={26} strokeWidth={1.75} />
            </span>
            <span className="tp-upload__drop-title">{selectedName || t('ai_docs_pick_file')}</span>
            <span className="tp-upload__drop-hint">{selectedName ? t('ai_docs_change_file') : t('ai_docs_formats')}</span>

            {selectedName && (
              <button
                type="button"
                className="tp-upload__clear"
                onClick={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                  if (fileInputRef.current) fileInputRef.current.value = '';
                  setSelectedName('');
                }}
                aria-label={t('ai_docs_remove_file')}
                title={t('ai_docs_remove_file')}
              >
                <X size={16} strokeWidth={2.25} aria-hidden="true" />
              </button>
            )}
          </label>

          <div className="tp-docs__upload-actions">
            <button
              type="button"
              className="btn btn--secondary"
              onClick={handleImportFolder}
              disabled={importing}
              title={t('ai_docs_import_title')}
            >
              <FolderDown size={16} strokeWidth={2} aria-hidden="true" />
              {importing ? t('ai_docs_importing') : t('ai_docs_import_folder')}
            </button>
            <button type="submit" className="btn btn--primary" disabled={uploading}>
              <UploadCloud size={16} strokeWidth={2} aria-hidden="true" />
              {uploading ? t('ai_docs_processing') : t('ai_docs_upload')}
            </button>
          </div>
        </form>

        {error && <p className="tp-form__alert" role="alert">{error}</p>}
        {success && <p className="tp-profile__ok" role="status">{success}</p>}

        {loading ? (
          <div className="tp-docs__loading"><Loader /></div>
        ) : docs.length === 0 ? (
          <div className="tp-dash__empty">
            <span className="tp-dash__empty-icon" aria-hidden="true">
              <FileText size={26} strokeWidth={1.75} />
            </span>
            <p className="tp-dash__empty-title">{t('ai_docs_empty')}</p>
            <p className="tp-dash__empty-text">{t('ai_docs_formats')}</p>
          </div>
        ) : (
          <>
            <p className="tp-docs__count">{t('ai_docs_loaded')} ({docs.length})</p>
            <ul className="tp-dash__list">
              {docs.map((doc) => (
                <li key={doc.id_documento} className="tp-dash__item">
                  <div className="tp-dash__main">
                    <div className="tp-dash__title-row">
                      <h3 className="tp-dash__title">{doc.nombre_archivo}</h3>
                      <span className="tp-docs__type">{(doc.tipo_archivo || '').toUpperCase()}</span>
                    </div>
                    <div className="tp-dash__meta">
                      <span className="tp-dash__dates">
                        <CalendarDays size={14} strokeWidth={2} aria-hidden="true" />
                        {formatDate(doc.fecha_subida)}
                      </span>
                      {doc.subido_por && (
                        <span className="tp-dash__dates">
                          <UserRound size={14} strokeWidth={2} aria-hidden="true" />
                          {doc.subido_por}
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="tp-dash__progress">
                    <span className="tp-dash__responses">
                      <HardDrive size={14} strokeWidth={2} aria-hidden="true" />
                      {formatBytes(doc.tamano)}
                    </span>
                    <span className="tp-dash__responses">
                      <FileText size={14} strokeWidth={2} aria-hidden="true" />
                      {t('ai_docs_text_extracted')}: {formatBytes(doc.bytes_texto)}
                    </span>
                  </div>

                  <div className="tp-dash__actions">
                    <button
                      type="button"
                      className="btn btn--outline btn--sm tp-dash__primary"
                      onClick={() => handleViewContent(doc.id_documento, doc.nombre_archivo)}
                    >
                      <Eye size={15} strokeWidth={2} aria-hidden="true" />
                      {t('ai_docs_view_content')}
                    </button>

                    <ActionMenu
                      label={t('dash_more_actions')}
                      groups={[{
                        key: 'doc',
                        items: [
                          {
                            key: 'view',
                            label: t('ai_docs_view_content'),
                            icon: <Eye size={16} />,
                            onClick: () => handleViewContent(doc.id_documento, doc.nombre_archivo)
                          },
                          {
                            key: 'delete',
                            label: t('common_delete'),
                            icon: <Trash2 size={16} />,
                            danger: true,
                            onClick: () => openDelete(doc)
                          }
                        ]
                      }]}
                    />
                  </div>
                </li>
              ))}
            </ul>
          </>
        )}

        <Modal
          open={Boolean(contentDoc)}
          onOpenChange={(open) => { if (!open) setContentDoc(null); }}
          variant="info"
          title={contentDoc ? contentDoc.nombre_archivo : ''}
          description={contentDoc && contentDoc.contenido
            ? `${(contentDoc.contenido.length || 0).toLocaleString()} ${t('ai_docs_chars')}`
            : t('ai_docs_text_extracted')}
          confirmText={t('common_close')}
          size="lg"
        >
          {contentLoading ? (
            <div className="tp-docs__loading"><Loader /></div>
          ) : !contentDoc || !contentDoc.contenido ? (
            <p className="tp-docs__hint">{t('ai_docs_empty_content')}</p>
          ) : (
            <>
              <button type="button" className="btn btn--outline btn--sm tp-docs__copy" onClick={handleCopyContent}>
                <Copy size={15} strokeWidth={2} aria-hidden="true" />
                {t('ai_docs_copy')}
              </button>
              <pre className="tp-docs__pre">{contentDoc.contenido}</pre>
            </>
          )}
        </Modal>

        <Modal
          open={Boolean(alert)}
          onOpenChange={(open) => { if (!open) setAlert(null); }}
          variant="warning"
          title={alert ? alert.title : ''}
          description={alert ? alert.text : ''}
          confirmText={t('common_understood')}
        />

        <Modal
          open={Boolean(docToDelete)}
          onOpenChange={(open) => { if (!open) setDocToDelete(null); }}
          variant="delete"
          title={t('ai_docs_delete_title')}
          description={docToDelete ? docToDelete.nombre_archivo : ''}
          cancelText={t('common_cancel')}
          confirmText={t('common_delete')}
          onCancel={() => setDocToDelete(null)}
          onConfirm={confirmDelete}
        />
      </div>
    </div>
  );
};

export default AiDocuments;
